/**
 * Aux Sensor tab — channel configuration.
 *
 * The tab compares same-type sensors (e.g. two power meters) against each other.
 * The source is the durable **gridded** data (`data_gridded`), fetched via the
 * `grid_data_aux_sensor` scope, where the pipeline already exposes the primary
 * and secondary selections of each type on the shared `TimeRel_tG00` axis:
 *
 *   Power   → RiderPwr_P (primary) / RiderAltPwr_P (secondary)
 *   Cadence → CrankCad_R (primary) / CrankAltCad_R (secondary)
 *   Speed   → WhlRspd_n  (primary) / WhlRspd_alt_n (secondary)
 *
 * Wheel speed can be shown rotational (raw rev/s) or linear (× wheel
 * circumference → km/h), and in linear mode GPS speed is overlaid for reference.
 * Dynamic pressure is an inactive stub for now.
 *
 * This module is pure (no React / network) so it can be unit-tested and reused.
 */
import { calculateMedian } from 'utils/statisticsUtils';
import { RunHealthObj } from 'types/Run';
import { AuxSensorLabels } from './auxSensorLabels';

export type AuxSensorType = 'power' | 'cadence' | 'speed' | 'wind';
export type SpeedMode = 'rotational' | 'linear';
export type DataMode = 'all' | 'traverse';

/** Shared relative-time axis for every gridded series. */
export const AUX_TIME_CHANNEL = 'TimeRel_tG00';

/** Windowed traverse lap channel: 0 outside a traverse, N within traverse N. */
export const TRAVERSE_CHANNEL = 'TraverseLap_NG10';

/** GPS speed gridded channels (m/s); first present wins. */
export const GPS_SPEED_CHANNELS = ['GpsSpeed_vG20', 'GpsSpeed_vG10'];

const MS_TO_KMH = 3.6;

/**
 * Cadence is stored rev/s and read by riders in rpm. The pipeline does the
 * same ×60 for its own display channel (`cdacalcterms.CrankCadRPM_RG3`), so
 * the stored form stays canonical and the conversion lives at the surface —
 * here, in `buildCadenceSeries`, and nowhere else.
 */
const REV_S_TO_RPM = 60;

export interface AuxSeriesDef {
  key: string;
  label: string;
  channel: string;
}

/**
 * Primary selection and supplementary instances of a sensor type. Position is
 * the single encoding of "which one is primary" (`series[0]`, as documented in
 * the module header) — a parallel boolean flag could silently disagree with it.
 * Only the auxiliary series are moved by the time-offset control; the primary
 * defines the reference timeline.
 */
export function splitPrimaryAux(series: AuxSeriesDef[]): {
  primary: AuxSeriesDef | undefined;
  aux: AuxSeriesDef[];
} {
  const [primary, ...aux] = series;
  return { primary, aux };
}

/**
 * The label a series is drawn with: the sensor's own name when the run's
 * records could identify it (`auxSensorLabels`), else the declared ordinal
 * one. Only the first two positions can be named — `primary` and its `Alt`
 * twin are the only two the pipeline writes per type.
 */
export function seriesLabel(
  def: AuxSeriesDef, index: number, labels?: AuxSensorLabels,
): string {
  if (index === 0) return labels?.primary?.label ?? def.label;
  if (index === 1) return labels?.alt?.label ?? def.label;
  return def.label;
}

/** A plotted series — points are `[relativeTimeSeconds, value]` (null = gap). */
export interface AuxPlotSeries {
  key: string;
  label: string;
  points: [number, number | null][];
  /** Draw points only (no connecting line) — e.g. raw per-event samples. */
  markersOnly?: boolean;
  /** Plot against the plot's secondary y-axis (AuxSensorPlot `secondaryAxis`). */
  secondaryAxis?: boolean;
}

export interface AuxPlotDef {
  type: AuxSensorType;
  title: string;
  unit: string;
  /** false → render an inactive placeholder (no data wired yet). */
  active: boolean;
  series: AuxSeriesDef[];
}

export const POWER_PLOT: AuxPlotDef = {
  type: 'power',
  title: 'Power Meter',
  unit: 'W',
  active: true,
  series: [
    { key: 'power-primary', label: 'Power Meter 1', channel: 'RiderPwr_P' },
    { key: 'power-secondary', label: 'Power Meter 2', channel: 'RiderAltPwr_P' },
  ],
};

export const CADENCE_PLOT: AuxPlotDef = {
  type: 'cadence',
  title: 'Cadence',
  unit: 'rpm',
  active: true,
  series: [
    { key: 'cadence-primary', label: 'Cadence 1', channel: 'CrankCad_R' },
    { key: 'cadence-secondary', label: 'Cadence 2', channel: 'CrankAltCad_R' },
  ],
};

/** Max magnitude (s) of the aux time-offset control. */
export const AUX_TIME_OFFSET_LIMIT_S = 2;

export const WIND_PLOT: AuxPlotDef = {
  type: 'wind',
  title: 'Dynamic Pressure',
  unit: 'Pa',
  active: false,
  series: [],
};

// Wheel-speed channels (rotational, rev/s). Linear mode scales by circumference.
const SPEED_SERIES: AuxSeriesDef[] = [
  { key: 'speed-primary', label: 'Wheel Speed 1', channel: 'WhlRspd_n' },
  { key: 'speed-secondary', label: 'Wheel Speed 2', channel: 'WhlRspd_alt_n' },
];

/**
 * Value channels each moving-average control smooths. A control smooths its own
 * sensor type wherever it appears, so e.g. the speed average reaches the wheel
 * speed plot, both slip-ratio plots and the wheel-speed comparison alike.
 */
export const POWER_SMOOTH_CHANNELS = POWER_PLOT.series.map((s) => s.channel);

/** Cadence-side counterpart of `POWER_SMOOTH_CHANNELS`, in stored rev/s. */
export const CADENCE_SMOOTH_CHANNELS = CADENCE_PLOT.series.map((s) => s.channel);

/** All gridded channels the tab needs — kept in sync with the server scope. */
export const AUX_SENSOR_GRID_CHANNELS = [
  AUX_TIME_CHANNEL,
  TRAVERSE_CHANNEL,
  ...POWER_SMOOTH_CHANNELS,
  ...CADENCE_SMOOTH_CHANNELS,
  ...SPEED_SERIES.map((s) => s.channel),
  ...GPS_SPEED_CHANNELS,
];

/** Speed-side counterpart of `POWER_SMOOTH_CHANNELS` — wheel sensors plus GPS. */
export const SPEED_SMOOTH_CHANNELS = [
  ...SPEED_SERIES.map((s) => s.channel),
  ...GPS_SPEED_CHANNELS,
];

type Gridded = Record<string, (number | null)[]> | null | undefined;

/**
 * Zip a value channel against the shared time channel into `[time, value]`
 * points, optionally scaling each value. `TimeRel_tG00` is already relative
 * seconds, so no time normalisation is needed. NaN/null samples are skipped.
 * Returns [] if the channel is absent or entirely empty.
 */
interface SeriesOpts {
  transform?: (v: number) => number;
  /** When set, samples failing the predicate become gaps (null) not points. */
  inWindow?: (i: number) => boolean;
  /** Seconds added to every timestamp — the aux time-offset alignment control. */
  timeShiftS?: number;
}

export function buildSeriesPoints(
  gridded: Gridded,
  channel: string,
  opts: SeriesOpts = {},
): [number, number | null][] {
  if (!gridded) return [];
  const time = gridded[AUX_TIME_CHANNEL];
  const values = gridded[channel];
  if (!time || !values) return [];
  const { transform, inWindow, timeShiftS = 0 } = opts;
  const gapped = !!inWindow;
  const n = Math.min(time.length, values.length);
  const points: [number, number | null][] = [];
  for (let i = 0; i < n; i += 1) {
    const raw = time[i];
    if (raw == null || Number.isNaN(raw)) continue;
    const t = raw + timeShiftS;
    const masked = inWindow ? !inWindow(i) : false;
    const v = values[i];
    if (masked || v == null || Number.isNaN(v)) {
      if (gapped) points.push([t, null]);
      continue;
    }
    points.push([t, transform ? transform(v) : v]);
  }
  return points;
}

/** Selectable moving-average widths, in grid samples. 0 = off. */
export const MOVING_AVERAGE_WINDOWS = [0, 3, 5, 7] as const;

/**
 * Centred moving average over an odd number of grid samples. Centred rather than
 * trailing so no phase lag is introduced — a trailing average would silently
 * shift sensor timing, which would fight the aux time-offset control.
 *
 * Three deliberate edge rules:
 *  - A null/NaN sample stays null. Smoothing must not invent a reading where the
 *    sensor was silent, so dropouts survive as dropouts.
 *  - The **ends are clipped**: the first and last `window/2` samples have no full
 *    window, so they are nulled rather than averaged over a partial one. That
 *    keeps every emitted value the same width of average as its neighbours.
 *  - An interior window that straddles a gap averages the valid samples it does
 *    cover. Nulling those too would widen every dropout by ±`window/2` and eat
 *    good data either side of it.
 */
export function movingAverage(values: (number | null)[], window: number): (number | null)[] {
  if (window <= 1) return values;
  const half = Math.floor(window / 2);
  const n = values.length;
  const out: (number | null)[] = new Array(n).fill(null);
  for (let i = half; i < n - half; i += 1) {
    const centre = values[i];
    if (centre == null || Number.isNaN(centre)) continue;
    let sum = 0;
    let count = 0;
    for (let j = i - half; j <= i + half; j += 1) {
      const v = values[j];
      if (v == null || Number.isNaN(v)) continue;
      sum += v;
      count += 1;
    }
    out[i] = count ? sum / count : null;
  }
  return out;
}

/**
 * Copy of `gridded` with the listed value channels replaced by their moving
 * average. Only the named channels are touched — never `TimeRel_tG00` (the axis)
 * or `TraverseLap_NG10` (a categorical lap index, meaningless to average).
 * Returns the input untouched when smoothing is off, so `useMemo` consumers keep
 * referential stability.
 */
export function smoothChannels(
  gridded: Record<string, (number | null)[]> | undefined,
  channels: string[],
  window: number,
): Record<string, (number | null)[]> | undefined {
  if (!gridded || window <= 1) return gridded;
  const out = { ...gridded };
  for (const channel of channels) {
    const values = gridded[channel];
    if (values) out[channel] = movingAverage(values, window);
  }
  return out;
}

/**
 * Sample period of the gridded data (s), read off `TimeRel_tG00` as the median
 * of its first few forward differences (median, not mean, so a single ragged
 * step can't skew it). The pipeline's default grid spacing is 0.25 s but it is a
 * per-session setting, so it is inferred rather than assumed. Returns 0 when the
 * time channel is missing or too short to tell.
 */
export function gridIntervalS(gridded: Gridded): number {
  const time = gridded?.[AUX_TIME_CHANNEL];
  if (!time) return 0;
  const diffs: number[] = [];
  let prev: number | null = null;
  for (let i = 0; i < time.length && diffs.length < 32; i += 1) {
    const t = time[i];
    if (t == null || Number.isNaN(t)) continue;
    if (prev != null && t > prev) diffs.push(t - prev);
    prev = t;
  }
  return calculateMedian(diffs);
}

/** `0.75` for a fraction, but a whole-second span reads `3` not `3.00`. */
export function formatSeconds(seconds: number): string {
  return Number.isInteger(seconds) ? seconds.toFixed(0) : seconds.toFixed(2);
}

/**
 * Radio options for a moving-average selector, labelled by span in seconds
 * alone — 3 samples on the default 0.25 s grid reads `0.75 s`.
 */
export function movingAverageOptions(intervalS: number): { value: string; label: string }[] {
  return MOVING_AVERAGE_WINDOWS.map((w) => ({
    value: String(w),
    label: w === 0 ? 'Off' : `${formatSeconds(w * intervalS)} s`,
  }));
}

/**
 * Convert a requested time offset (s) into a whole number of grid samples. The
 * comparison scatter can only re-pair samples by index, so the offset is snapped
 * to the grid; multiply back by `intervalS` to get the offset actually applied,
 * keeping the time plot and the scatter exactly consistent.
 */
export function timeOffsetToSamples(offsetS: number, intervalS: number): number {
  if (!intervalS) return 0;
  return Math.round(offsetS / intervalS);
}

/** Predicate: is sample i inside a traverse window (TraverseLap_NG10 > 0)? */
export function traverseMask(gridded: Gridded): ((i: number) => boolean) | undefined {
  const lap = gridded?.[TRAVERSE_CHANNEL];
  if (!lap) return undefined;
  return (i: number) => {
    const v = lap[i];
    return v != null && !Number.isNaN(v) && v > 0;
  };
}

export function hasTraverseData(gridded: Gridded): boolean {
  const lap = gridded?.[TRAVERSE_CHANNEL];
  return !!lap && lap.some((v) => v != null && !Number.isNaN(v) && v > 0);
}

export interface TraverseSegment {
  lap: number;
  startS: number;
  endS: number;
}

/** Contiguous nonzero runs of TraverseLap_NG10, with TimeRel start/end (s). */
export function buildTraverseSegments(gridded: Gridded): TraverseSegment[] {
  const time = gridded?.[AUX_TIME_CHANNEL];
  const lap = gridded?.[TRAVERSE_CHANNEL];
  const segments: TraverseSegment[] = [];
  if (!time || !lap) return segments;
  const n = Math.min(time.length, lap.length);
  let cur = 0;
  let startS = 0;
  let lastT = 0;
  for (let i = 0; i < n; i += 1) {
    const t = time[i];
    if (t == null || Number.isNaN(t)) continue;
    const raw = lap[i];
    const v = raw != null && !Number.isNaN(raw) && raw > 0 ? raw : 0;
    if (v !== cur) {
      if (cur > 0) segments.push({ lap: cur, startS, endS: lastT });
      cur = v;
      startS = t;
    }
    lastT = t;
  }
  if (cur > 0) segments.push({ lap: cur, startS, endS: lastT });
  return segments;
}

function firstPresentChannel(gridded: Gridded, channels: string[]): string | null {
  if (!gridded) return null;
  for (const ch of channels) {
    const arr = gridded[ch];
    if (arr && arr.some((v) => v != null && !Number.isNaN(v))) return ch;
  }
  return null;
}

const hasValue = (s: AuxPlotSeries) => s.points.some((p) => p[1] != null);

/**
 * The shape every per-instance overlay on this tab shares: one line per
 * declared instance, primary first, auxiliaries optionally time-shifted, and
 * instances with no data dropped rather than drawn as an empty line. Power and
 * cadence differ only in their channel set and whether the stored values need
 * a display conversion.
 */
function buildDefSeries(
  def: AuxPlotDef,
  gridded: Gridded,
  dataMode: DataMode,
  opts: {
    auxTimeOffsetS?: number;
    transform?: (v: number) => number;
    labels?: AuxSensorLabels;
  } = {},
): AuxPlotSeries[] {
  const inWindow = dataMode === 'traverse' ? traverseMask(gridded) : undefined;
  const { primary } = splitPrimaryAux(def.series);
  const { auxTimeOffsetS = 0, transform, labels } = opts;
  return def.series
    .map((s, i) => ({
      key: s.key,
      label: seriesLabel(s, i, labels),
      points: buildSeriesPoints(gridded, s.channel, {
        transform,
        inWindow,
        timeShiftS: s === primary ? 0 : auxTimeOffsetS,
      }),
    }))
    .filter(hasValue);
}

/**
 * Power-meter series. `auxTimeOffsetS` re-stamps the auxiliary meters (the 2nd,
 * 3rd, … instances) by that many seconds, leaving the primary meter's timeline
 * fixed — the same alignment the comparison scatter applies by sample index.
 */
export function buildPowerSeries(
  gridded: Gridded,
  dataMode: DataMode = 'all',
  auxTimeOffsetS = 0,
  labels?: AuxSensorLabels,
): AuxPlotSeries[] {
  return buildDefSeries(POWER_PLOT, gridded, dataMode, { auxTimeOffsetS, labels });
}

/**
 * Cadence series, converted rev/s → rpm as they are built. Smoothing runs on
 * the stored rev/s and a moving average is linear, so the two operations
 * commute and the plotted rpm is the same either way.
 *
 * No time-offset parameter: that control exists to re-pair sample indices for
 * the power comparison scatter, and cadence has no scatter — two cadence
 * sources measure the same crank, so there is no gain to calibrate between
 * them the way there is between two power meters.
 */
export function buildCadenceSeries(
  gridded: Gridded,
  dataMode: DataMode = 'all',
  labels?: AuxSensorLabels,
): AuxPlotSeries[] {
  return buildDefSeries(CADENCE_PLOT, gridded, dataMode, {
    transform: (v) => v * REV_S_TO_RPM,
    labels,
  });
}

/**
 * Wheel-speed series. Rotational → raw rev/s. Linear → rev/s × wheel
 * circumference (m) × 3.6 = km/h, with GPS speed (m/s × 3.6) overlaid. In
 * traverse mode, samples outside a traverse window are masked to gaps.
 */
export function buildSpeedSeries(
  gridded: Gridded,
  mode: SpeedMode,
  circumference: number,
  dataMode: DataMode = 'all',
  labels?: AuxSensorLabels,
): AuxPlotSeries[] {
  const inWindow = dataMode === 'traverse' ? traverseMask(gridded) : undefined;
  if (mode === 'rotational') {
    return SPEED_SERIES
      .map((s, i) => ({
        key: s.key,
        label: seriesLabel(s, i, labels),
        points: buildSeriesPoints(gridded, s.channel, { inWindow }),
      }))
      .filter(hasValue);
  }
  const toKmh = (v: number) => v * circumference * MS_TO_KMH;
  const series: AuxPlotSeries[] = SPEED_SERIES.map((s, i) => ({
    key: s.key,
    label: seriesLabel(s, i, labels),
    points: buildSeriesPoints(gridded, s.channel, { transform: toKmh, inWindow }),
  }));
  const gpsChannel = firstPresentChannel(gridded, GPS_SPEED_CHANNELS);
  if (gpsChannel) {
    series.push({
      key: 'speed-gps',
      label: 'GPS Speed',
      points: buildSeriesPoints(gridded, gpsChannel, { transform: (v) => v * MS_TO_KMH, inWindow }),
    });
  }
  return series.filter(hasValue);
}

export function speedUnit(mode: SpeedMode): string {
  return mode === 'linear' ? 'km/h' : 'rev/s';
}

/** One scatter series for the comparison plot. */
export interface AuxScatterSeries {
  key: string;
  name: string;
  points: [number, number][];
}

export interface AuxComparison {
  series: AuxScatterSeries[];
  /** Upper bound for the (square) axes and the 1:1 reference line. */
  axisMax: number;
}

/**
 * Wheel-sensor agreement scatter. X = wheel speed 1; Y = wheel speed 2 and GPS
 * speed (both vs sensor 1). Everything is converted to km/h so the wheel sensors
 * and GPS are directly comparable; points on the 1:1 diagonal mean agreement.
 * Channels are index-aligned on the shared grid, so samples zip by index.
 */
export function buildComparisonScatter(
  gridded: Gridded,
  circumference: number,
  dataMode: DataMode = 'all',
  labels?: AuxSensorLabels,
): AuxComparison {
  const toKmh = (v: number) => v * circumference * MS_TO_KMH;
  const w1 = gridded?.WhlRspd_n;
  const w2 = gridded?.WhlRspd_alt_n;
  const gpsChannel = firstPresentChannel(gridded, GPS_SPEED_CHANNELS);
  const gps = gpsChannel && gridded ? gridded[gpsChannel] : undefined;
  const inWindow = dataMode === 'traverse' ? traverseMask(gridded) : undefined;

  const vsW2: [number, number][] = [];
  const vsGps: [number, number][] = [];
  let axisMax = 0;

  if (w1) {
    for (let i = 0; i < w1.length; i += 1) {
      const x = w1[i];
      if (x == null || Number.isNaN(x)) continue;
      if (inWindow && !inWindow(i)) continue;
      const xk = toKmh(x);
      const w2v = w2?.[i];
      if (w2v != null && !Number.isNaN(w2v)) {
        const yk = toKmh(w2v);
        vsW2.push([xk, yk]);
        axisMax = Math.max(axisMax, xk, yk);
      }
      const gpsv = gps?.[i];
      if (gpsv != null && !Number.isNaN(gpsv)) {
        const yk = gpsv * MS_TO_KMH;
        vsGps.push([xk, yk]);
        axisMax = Math.max(axisMax, xk, yk);
      }
    }
  }

  const series: AuxScatterSeries[] = [];
  if (vsW2.length) {
    series.push({ key: 'cmp-w2', name: labels?.alt?.label ?? 'Wheel Speed 2', points: vsW2 });
  }
  if (vsGps.length) series.push({ key: 'cmp-gps', name: 'GPS Speed', points: vsGps });
  return { series, axisMax: Math.ceil(axisMax) };
}

/**
 * Power-meter agreement scatter: x = the primary meter, y = each auxiliary meter
 * (all in W). One series per auxiliary instance, so a third power meter shows up
 * automatically once its channel is added to `POWER_PLOT.series`.
 *
 * `auxSampleShift` re-pairs the auxiliary samples by that many grid samples —
 * the index-space form of the time-offset control. A shift of `+k` means the aux
 * timestamps moved *later* by k samples, so the primary sample at index `j` is
 * compared against the aux sample at index `j - k`.
 */
export function buildPowerComparisonScatter(
  gridded: Gridded,
  dataMode: DataMode = 'all',
  auxSampleShift = 0,
  labels?: AuxSensorLabels,
): AuxComparison {
  const { primary, aux } = splitPrimaryAux(POWER_PLOT.series);
  const p1 = primary && gridded ? gridded[primary.channel] : undefined;
  const inWindow = dataMode === 'traverse' ? traverseMask(gridded) : undefined;
  const series: AuxScatterSeries[] = [];
  let axisMax = 0;
  if (p1) {
    for (const [n, def] of aux.entries()) {
      const pAux = gridded?.[def.channel];
      if (!pAux) continue;
      const points: [number, number][] = [];
      for (let j = 0; j < p1.length; j += 1) {
        // The traverse window is judged on the reference timeline (index j).
        if (inWindow && !inWindow(j)) continue;
        const k = j - auxSampleShift;
        if (k < 0 || k >= pAux.length) continue;
        const a = p1[j];
        const b = pAux[k];
        if (a == null || b == null || Number.isNaN(a) || Number.isNaN(b)) continue;
        points.push([a, b]);
        axisMax = Math.max(axisMax, a, b);
      }
      // Only the first auxiliary meter is `Alt` — a third would need its
      // own channel set, and its own name, before it could be labelled.
      const name = n === 0 ? labels?.alt?.label ?? def.label : def.label;
      if (points.length) series.push({ key: `pwr-cmp-${def.key}`, name, points });
    }
  }
  return { series, axisMax: Math.ceil(axisMax) };
}

/**
 * Dense split of scatter points into those a threshold keeps and those it drops.
 * The excluded list is carried rather than discarded so the plot can grey those
 * samples out instead of hiding them. Shares the `included`/`excluded` vocabulary
 * with `partitionByRange` and `buildRatioTimeSeries`.
 */
export interface ScatterSplit {
  included: [number, number][];
  excluded: [number, number][];
}

/**
 * Slip-vs-tractive-force scatter for the two wheel sensors.
 *   x = tyre driving (tractive) force = RiderPwr_P / (WhlRspd_n × circumference)
 *       — power / ground-speed = N, since P = F·v.
 *   y = WhlRspd_n / WhlRspd_alt_n — the slip ratio between the two sensors.
 * Samples whose sensor-1 linear speed is below `speedThresholdMs` are masked: at
 * very low speed both the force (÷v) and the ratio explode into noise. They are
 * returned as `excluded` so the plot can grey them out instead of hiding them.
 * Samples where the force or ratio is undefined (zero speed, zero wheel-2 rate)
 * are dropped from both lists.
 */
export function buildRatioForceScatter(
  gridded: Gridded,
  circumference: number,
  speedThresholdMs: number,
  dataMode: DataMode = 'all',
): ScatterSplit {
  const w1 = gridded?.WhlRspd_n;
  const w2 = gridded?.WhlRspd_alt_n;
  const pwr = gridded?.RiderPwr_P;
  const inWindow = dataMode === 'traverse' ? traverseMask(gridded) : undefined;
  const included: [number, number][] = [];
  const excluded: [number, number][] = [];

  if (w1 && w2 && pwr) {
    const n = Math.min(w1.length, w2.length, pwr.length);
    for (let i = 0; i < n; i += 1) {
      const r1 = w1[i];
      const r2 = w2[i];
      const p = pwr[i];
      if (r1 == null || r2 == null || p == null) continue;
      if (Number.isNaN(r1) || Number.isNaN(r2) || Number.isNaN(p)) continue;
      if (inWindow && !inWindow(i)) continue;
      const speedMs = r1 * circumference;
      if (speedMs === 0 || r2 === 0) continue;
      (speedMs < speedThresholdMs ? excluded : included).push([p / speedMs, r1 / r2]);
    }
  }
  return { included, excluded };
}

/** Max wheel-1 linear speed (m/s) over the run — the slip-ratio slider's range. */
export function maxWheelSpeedMs(gridded: Gridded, circumference: number): number {
  const w1 = gridded?.WhlRspd_n;
  if (!w1) return 0;
  let max = 0;
  for (const v of w1) {
    if (v == null || Number.isNaN(v)) continue;
    const speedMs = v * circumference;
    if (speedMs > max) max = speedMs;
  }
  return Math.ceil(max);
}

/**
 * Time-series counterpart of `ScatterSplit`. Both arrays are full grid length and
 * null outside their own stretch — unlike the dense scatter split, a time series
 * must keep its gaps so the two lines neither bridge nor overlap.
 */
export interface TimeSeriesSplit {
  /** Slip ratio where the sample passed the min-speed threshold; null elsewhere. */
  included: [number, number | null][];
  /** Slip ratio where the threshold masked the sample out; null elsewhere. */
  excluded: [number, number | null][];
}

/**
 * Slip ratio (WhlRspd_n / WhlRspd_alt_n) versus time, split into the stretches
 * that pass the min-speed threshold and the stretches it masks. Both arrays carry
 * one entry per grid sample, with null outside their own stretch, so each renders
 * as a gapped line that neither bridges its own gaps nor overlaps the other —
 * letting the masked low-speed stretches be drawn greyed-out rather than hidden.
 */
export function buildRatioTimeSeries(
  gridded: Gridded,
  circumference: number,
  speedThresholdMs: number,
  dataMode: DataMode = 'all',
): TimeSeriesSplit {
  const time = gridded?.[AUX_TIME_CHANNEL];
  const w1 = gridded?.WhlRspd_n;
  const w2 = gridded?.WhlRspd_alt_n;
  const inWindow = dataMode === 'traverse' ? traverseMask(gridded) : undefined;
  const included: [number, number | null][] = [];
  const excluded: [number, number | null][] = [];
  if (time && w1 && w2) {
    const n = Math.min(time.length, w1.length, w2.length);
    for (let i = 0; i < n; i += 1) {
      const t = time[i];
      if (t == null || Number.isNaN(t)) continue;
      const r1 = w1[i];
      const r2 = w2[i];
      let ratio: number | null = null;
      const inTraverse = !inWindow || inWindow(i);
      if (inTraverse && r1 != null && r2 != null && !Number.isNaN(r1) && !Number.isNaN(r2) && r2 !== 0) {
        ratio = r1 / r2;
      }
      const belowThreshold = r1 == null || Number.isNaN(r1) || r1 * circumference < speedThresholdMs;
      included.push([t, belowThreshold ? null : ratio]);
      excluded.push([t, belowThreshold ? ratio : null]);
    }
  }
  return { included, excluded };
}

/** Ordinary-least-squares fit of y on x, plus the coefficient of determination. */
export interface LinearFit {
  slope: number;
  /** y-intercept — the "offset" of the fit. */
  intercept: number;
  /** R², in [0, 1]. */
  r2: number;
  /** Number of points the fit was computed over. */
  count: number;
}

/**
 * Least-squares fit of `y = slope·x + intercept` over the given scatter points.
 * Returns null when there is nothing meaningful to fit (fewer than two points,
 * or every point sharing the same x, which leaves the slope undefined).
 */
export function linearFit(points: [number, number][]): LinearFit | null {
  const count = points.length;
  if (count < 2) return null;
  let sumX = 0;
  let sumY = 0;
  for (const [x, y] of points) {
    sumX += x;
    sumY += y;
  }
  const meanX = sumX / count;
  const meanY = sumY / count;
  let sxx = 0;
  let sxy = 0;
  for (const [x, y] of points) {
    const dx = x - meanX;
    sxx += dx * dx;
    sxy += dx * (y - meanY);
  }
  if (sxx === 0) return null;
  const slope = sxy / sxx;
  const intercept = meanY - slope * meanX;
  return { slope, intercept, r2: rSquared(points, slope, intercept), count };
}

/**
 * Coefficient of determination of `y = slope·x + intercept` against the data,
 * as `1 − SSres/SStot`. Shared by both fits so a tuning change to one can't leave
 * the two readouts meaning different things — for the free fit this is exactly
 * equivalent to `sxy²/(sxx·syy)`. A constrained fit can be worse than a flat
 * line, which would give a negative value, so the result is clamped at 0.
 */
function rSquared(points: [number, number][], slope: number, intercept: number): number {
  let sumY = 0;
  for (const [, y] of points) sumY += y;
  const meanY = sumY / points.length;
  let ssRes = 0;
  let ssTot = 0;
  for (const [x, y] of points) {
    const residual = y - (slope * x + intercept);
    ssRes += residual * residual;
    ssTot += (y - meanY) * (y - meanY);
  }
  if (ssTot === 0) return ssRes === 0 ? 1 : 0;
  return Math.max(0, 1 - ssRes / ssTot);
}

/**
 * Least-squares fit with the intercept held at `intercept`, so only the slope is
 * free. This is the "manual offset" mode: pinning the offset (usually to zero)
 * makes the slope a pure gain ratio between the two sensors rather than one leg
 * of a slope/offset pair that can trade off against each other.
 *
 * R² is reported for the *constrained* model (1 − SSres/SStot about mean y), so
 * unlike the free fit it can be worse than a flat line; it is clamped at 0.
 */
export function linearFitFixedIntercept(
  points: [number, number][],
  intercept: number,
): LinearFit | null {
  const count = points.length;
  if (count < 2) return null;
  let sxx = 0;
  let sxy = 0;
  for (const [x, y] of points) {
    sxx += x * x;
    sxy += x * (y - intercept);
  }
  if (sxx === 0) return null;
  const slope = sxy / sxx;
  return { slope, intercept, r2: rSquared(points, slope, intercept), count };
}

/** X range of a scatter series, or null when there is nothing to span. */
export function xExtent(points: [number, number][]): [number, number] | null {
  if (!points.length) return null;
  let lo = points[0][0];
  let hi = lo;
  for (const [x] of points) {
    if (x < lo) lo = x;
    if (x > hi) hi = x;
  }
  return [lo, hi];
}

/** Two-point line spanning the fitted points — the drawable form of a `LinearFit`. */
export function fitLinePoints(
  points: [number, number][],
  fit: LinearFit | null,
): [number, number][] | null {
  const extent = fit && xExtent(points);
  if (!extent) return null;
  return extent.map((x) => [x, fit.slope * x + fit.intercept] as [number, number]);
}

/**
 * Split scatter points on the x-axis window used by the fit filters: points
 * inside [`minX`, `maxX`] are fitted/plotted normally, the rest are kept
 * separately so they can be drawn greyed-out rather than dropped. `maxX` defaults
 * to unbounded, which is the min-only form used by the slip-ratio plot.
 */
export function partitionByRange(
  points: [number, number][],
  minX: number,
  maxX = Infinity,
): ScatterSplit {
  if (!minX && maxX === Infinity) return { included: points, excluded: [] };
  const included: [number, number][] = [];
  const excluded: [number, number][] = [];
  for (const p of points) {
    (p[0] >= minX && p[0] <= maxX ? included : excluded).push(p);
  }
  return { included, excluded };
}

/** Largest x value across a scatter series — the fit slider's upper bound. */
export function maxXValue(points: [number, number][]): number {
  return Math.ceil(xExtent(points)?.[1] ?? 0);
}

/**
 * Whether a run has more than one of any aux sensor type, i.e. whether the
 * "Aux Sensor" tab should be shown. Reads `run_health_obj.sensors_present`,
 * whose `power_meter_2/_3` and `speed_2/_3` keys appear only when those
 * supplementary sensors were detected — no extra network call required.
 */
export function hasAuxSensors(
  run: { run_health_obj?: RunHealthObj | null } | null | undefined,
): boolean {
  const sp = run?.run_health_obj?.sensors_present as Record<string, unknown> | undefined;
  if (!sp) return false;
  return ['power_meter_2', 'power_meter_3', 'speed_2', 'speed_3', 'wind_2', 'wind_3', 'aero_2', 'aero_3']
    .some((key) => !!sp[key]);
}
